import { useState, useEffect, useRef } from "react"; // Import useRef

/**
 * Custom React hook to fetch real-time cryptocurrency price from Binance WebSocket.
 * @param {string} cryptoSymbol - The symbol of the cryptocurrency (e.g., 'BTC', 'ETH').
 * @returns {string | null} The real-time price as a string, or null if loading or error.
 */
const useRealTimePrice = (cryptoSymbol) => {
	const [price, setPrice] = useState(null);
	// Use useRef to store the WebSocket instance
	const wsRef = useRef(null);

	// Derive the symbol string, ensuring it's only computed when cryptoSymbol changes
	const symbol = `${cryptoSymbol?.toLowerCase()}usdt`;

	useEffect(() => {
		// --- Cleanup Function (defined first for clarity) ---
		// This function will run when the component unmounts or dependencies change.
		const cleanup = () => {
			if (wsRef.current) {
				// Check if the WebSocket is open before closing
				if (wsRef.current.readyState === WebSocket.OPEN) {
					wsRef.current.close();
				}
				wsRef.current = null; // Clear the ref after closing
			}
			setPrice(null); // Reset price on cleanup
		};

		// --- Effect Logic ---
		// Check if cryptoSymbol is provided before attempting to connect
		if (!cryptoSymbol) {
			// If no symbol, clean up any existing connection and state
			cleanup();
			console.warn("useRealTimePrice: No cryptoSymbol provided.");
			return; // Do not establish WebSocket connection
		}

		// Construct the WebSocket URL for the ticker stream
		const wsUrl = `wss://stream.binance.com:9443/ws/${symbol}@ticker`;

		// Close any existing connection before creating a new one (important when symbol changes)
		cleanup();

		// Create a new WebSocket instance and store it in the ref
		const ws = new WebSocket(wsUrl);
		wsRef.current = ws;

		// Event handler for incoming messages
		ws.onmessage = (event) => {
			try {
				const data = JSON.parse(event.data);
				// 'c' is the current price in the Binance ticker stream
				if (data && data.c !== undefined) {
					// Explicitly check for undefined
					setPrice(data.c);
				}
				// You could add other checks here if you expect different message types
			} catch (error) {
				console.error(
					`useRealTimePrice: Error parsing WebSocket message for ${symbol}:`,
					error
				);
				// Decide if you want to set price to null on parsing error.
				// Keeping the last valid price might be preferable unless the error is persistent.
				// setPrice(null);
			}
		};

		// Event handler for WebSocket errors
		ws.onerror = (error) => {
			console.error(`useRealTimePrice: WebSocket error for ${symbol}:`, error);
			// It's often good practice to set price to null or an error state on connection errors
			setPrice(null);
			// Consider implementing a simple reconnection attempt here or in the close handler
		};

		// Event handler for WebSocket closure
		ws.onclose = (event) => {
			console.log(
				`useRealTimePrice: WebSocket for ${symbol} closed: Code=${event.code}, Reason=${event.reason}`
			);
			// Decide if you want to set price to null on close.
			// If it's an unexpected close, you might want to indicate a problem.
			setPrice(null);
			wsRef.current = null; // Clear the ref when the connection is closed
			// Implement reconnection logic here if needed and appropriate for your app
			// Example: if (event.code !== 1000) { attemptReconnect(); }
		};

		// --- Return Cleanup Function ---
		return cleanup;
	}, [symbol, cryptoSymbol]); // Dependencies: Re-run effect if symbol or cryptoSymbol changes

	// The hook only returns the state variable holding the price
	return price;
};

export default useRealTimePrice;
